import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import InboxIcon from '@material-ui/icons/MoveToInbox';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import Tooltip from '~components/tooltip';
import { useReplaceSuiteStudentToCustomer } from '~containers/suite-config/suite-context';
import {
  ArrowV,
  BasicBuildingFilled,
  BasicGroup,
  BasicMedal,
  NavAdmin,
  NavDashboard,
  NavEntries,
  NavRooms,
  NavStudent,
} from '~svg-components';
import { useSideNavigationOpenContext } from '../side-navigation-context';

const extracted = (theme) => ({
  button: {
    '&:hover': {
      backgroundColor: theme.colors.greyFa,
    },
  },

  listRoot: {
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: 5,
    marginBottom: 5,
    '&:hover': {
      backgroundColor: theme.colors.greyFa,
    },
  },

  listRootMarginBottomForDivider: {
    marginBottom: 12,
    '& + ul': {
      marginTop: 24,
    },
  },

  gutters: {
    position: 'relative',
    padding: 0,

    '&::before': {
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      content: "''",
      width: '4px',
      height: '24px',
      borderRadius: '2px',
      marginRight: '20px',
    },
  },
  outerGutters: {
    paddingLeft: 28,
    paddingRight: 19,
  },
  outerSelected: {
    color: theme.colors.brandAction,
    '& .MuiListItemIcon-root': {
      color: theme.colors.brandAction,
      fill: theme.colors.brandAction,
    },
    '&.Mui-selected': {
      backgroundColor: theme.colors.transparent,

      '&::before': {
        backgroundColor: theme.colors.brandIcon,
      },
      '&:hover': {
        backgroundColor: theme.colors.greyFa,
      },
    },

    // Sub item
    '& + .MuiCollapse-container .Mui-selected': {
      backgroundColor: theme.colors.brandBg,

      '&::before': {
        backgroundColor: theme.colors.transparent,
      },
    },
  },
  selected: {
    color: theme.colors.brandAction,
    '&.Mui-selected': {
      backgroundColor: theme.colors.transparent,

      '&::before': {
        backgroundColor: theme.colors.brandIcon,
      },
      '&:hover': {
        backgroundColor: theme.colors.greyFa,
      },
    },
  },
  keyboardArrow: {
    width: 10,
    height: 10,
    color: theme.colors.grey99,
    transition: '0.3s',
  },
  keyboardArrowDown: {
    transform: 'rotate(90deg)',
  },
  iconRoot: {
    display: 'block',
    minWidth: 34,
    fill: theme.colors.grey66,
    padding: '11px 22px',
  },
  iconRootSideNavigationOpen: {
    display: 'block',
    minWidth: 34,
    fill: theme.colors.grey66,
    padding: '11px 22px',
    marginTop: 2,
  },
  iconRootSetting: {
    display: 'block',
    minWidth: 34,
    padding: '11px 22px',
    fill: theme.colors.grey66,
  },
  iconRootBottom12: {
    display: 'block',
    minWidth: 34,
    fill: theme.colors.grey66,
    padding: '11px 22px',
  },
  iconRootWithDivider: {
    position: 'relative',
  },
  hide: {
    opacity: 0,
  },
  cuttingLine: {
    position: 'absolute',
    bottom: -24,
    left: '37%',
    width: '16px',
    height: '2px',
    backgroundColor: theme.colors.greyE8,
    opacity: 0.6,
    borderRadius: '2px',
  },
  cuttingLineOpenMenu: {
    width: '208px',
    left: '-4px',
    height: '1px',
  },
  cuttingLineOpenSecondMenu: {
    width: '208px',
    left: '20px',
    height: '1px',
    position: 'absolute',
    bottom: -24,
    backgroundColor: theme.colors.greyE8,
    opacity: 0.6,
    borderRadius: '2px',
  },
  theme: {
    width: 20,
    height: 20,
    fill: theme.colors.grey66,
  },
  selectedTheme: {
    width: 20,
    height: 20,
    fill: theme.colors.brandAction,
  },
  hideTooltip: { display: 'none' },
  secondaryMenuListText: { paddingLeft: 22 },
});
const commonStyles = extracted;

const settingStyles = makeStyles((theme) => ({
  ...commonStyles(theme),
  theme: {
    width: 20,
    height: 20,
    fill: theme.colors.grey66,
  },
  selectedTheme: {
    width: 20,
    height: 20,
    fill: theme.colors.brandAction,
  },
  setting: {
    backgroundColor: theme.colors.white,
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: 62,
  },
  settingOpen: {
    width: 246,
  },
  divider: {
    margin: '0 0.5rem',
  },
  iconRootSetting: {
    display: 'block',
    minWidth: 34,
    fill: theme.colors.grey66,
    padding: '11px 22px',
  },
  outerGutters: {
    paddingLeft: 28,
    paddingRight: 19,
  },
}));

const Setting = ({ name, linkTo, selected, hide }) => {
  const classes = settingStyles();

  return (
    <div
      className={classnames(classes.setting, {
        [classes.settingOpen]: !hide,
      })}
    >
      <List>
        <Tooltip
          title={name}
          placement="right"
          classes={{ popper: !hide ? classes.hideTooltip : '' }}
        >
          <ListItem
            button
            onClick={linkTo}
            classes={{
              gutters: classnames(classes.gutters, {
                [classes.outerGutters]: !hide,
              }),
              selected: classes.selected,
              root: classes.root,
              button: classes.button,
            }}
            selected={selected}
          >
            <ListItemIcon classes={{ root: classes.iconRootSetting }}>
              <NavAdmin className={selected ? classes.selectedTheme : classes.theme} />
            </ListItemIcon>
            <ListItemText
              className={hide ? classes.hide : ''}
              primary={name}
              aria-controls={name}
            />
          </ListItem>
        </Tooltip>
      </List>
    </div>
  );
};

Setting.propTypes = {
  name: PropTypes.string.isRequired,
  linkTo: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
  hide: PropTypes.bool.isRequired,
};

const pushUrl = (parentName, name) => `/${parentName ? `${parentName}/` : ''}${name}`;

const useStyles = (locate) =>
  makeStyles((theme) => {
    return {
      ...commonStyles(theme),
      nested: {
        paddingLeft: theme.spacing(4 * locate),
      },
      tooltipInner: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
      },
      fixedWidth: {
        display: 'inline-block',
        // overflow: 'hidden',
      },
    };
  });

export default function ListSlide({
  slug: name,
  childPermissions: children,
  locate,
  auth,
  parentName,
  isSecondaryMenu,
  // isLastOnSecondaryMenu,
}) {
  const history = useHistory();
  const { pathname } = useLocation();
  const { isSideNavigationOpen } = useSideNavigationOpenContext();
  const replaceSuiteStudentToCustomer = useReplaceSuiteStudentToCustomer();

  const concatName = `${parentName ? `${parentName}/` : ''}${name}`;
  const [open, setOpen] = useState(false);
  const classes = useStyles(locate)();
  const [anchorEl, setAnchorEl] = useState(null);
  const [t] = useTranslation();
  const realName =
    name === 'student'
      ? replaceSuiteStudentToCustomer(t(`functions.${name}`), t(`functions.${name}.CUSTOMER`))
      : t(`functions.${name}`);

  const selected = pathname.split('/').filter((item) => item === name).length > 0;

  const handleClose = (url, state) => {
    setAnchorEl(null);
    if (typeof url === 'string' && url) {
      history.push({
        pathname: url,
        state,
      });
    }
  };
  const hideIcon = !['student', 'booking', 'partners', 'nominations'].includes(name);
  function handleDrawerOpen(event) {
    if (name === 'student' || name === 'booking' || name === 'partners' || name === 'nominations') {
      return history.push({
        pathname: `/${name}`,
        state: { auth },
      });
    }
    if (locate > 0) {
      return history.push({
        pathname: pushUrl(parentName, name),
        state: { auth },
      });
    }
    if (!isSideNavigationOpen && children) {
      return setAnchorEl(event.currentTarget);
    }
    if (children) {
      return setOpen((prevOpen) => !prevOpen);
    }
    return history.push({
      pathname: pushUrl(parentName, name),
      state: { auth },
    });
  }
  useEffect(() => {
    if (!isSideNavigationOpen) {
      setOpen(false);
    }
  }, [isSideNavigationOpen]);

  const isShowUnderline = useMemo(() => name === 'in-residence' || name === 'partners', [name]);

  if (name === 'settings') {
    return (
      <Setting
        hide={!isSideNavigationOpen}
        name={t('functions.settings')}
        linkTo={() => history.push(`/${name}`)}
        // Setting can't be selected, requirement from designer
        selected={false}
      />
    );
  }
  const renderIcon = () => {
    if (locate) {
      return <div />;
    }
    if (name === 'dashboard') {
      return <NavDashboard className={selected ? classes.selectedTheme : classes.theme} />;
    }
    if (name === 'inventory') {
      return <NavRooms className={selected ? classes.selectedTheme : classes.theme} />;
    }
    if (name === 'in-residence') {
      return <BasicBuildingFilled className={selected ? classes.selectedTheme : classes.theme} />;
    }
    if (name === 'student') {
      return <NavStudent className={selected ? classes.selectedTheme : classes.theme} />;
    }
    if (name === 'booking') {
      return <NavEntries className={selected ? classes.selectedTheme : classes.theme} />;
    }
    if (name === 'nominations') {
      return <BasicMedal className={selected ? classes.selectedTheme : classes.theme} />;
    }
    if (name === 'partners') {
      return <BasicGroup className={selected ? classes.selectedTheme : classes.theme} />;
    }
    return <InboxIcon />;
  };
  return (
    name !== 'approval-center' && (
      <List
        className={classnames(classes.listRoot, {
          [classes.listRootMarginBottomForDivider]: name === 'in-residence' || name === 'partners',
        })}
      >
        <ListItem
          button
          disableRipple
          onClick={handleDrawerOpen}
          classes={{
            gutters: classnames(classes.gutters, {
              [classes.outerGutters]: isSideNavigationOpen,
            }),
            selected:
              children && isSideNavigationOpen && locate < 1
                ? classes.outerSelected
                : classes.selected,
            root: classes.root,
            button: classes.button,
          }}
          selected={selected}
          title={realName}
        >
          <Tooltip
            title={realName}
            placement="right"
            classes={{ popper: isSideNavigationOpen ? classes.hideTooltip : '' }}
          >
            <div
              className={classnames(classes.tooltipInner, {
                [classes.fixedWidth]: !isSideNavigationOpen,
              })}
            >
              <div className={classes.iconRootWithDivider}>
                <ListItemIcon
                  classes={{
                    root:
                      isShowUnderline && !isSideNavigationOpen
                        ? classes.iconRootBottom12
                        : isSideNavigationOpen
                        ? classes.iconRootSideNavigationOpen
                        : classes.iconRoot,
                  }}
                >
                  {renderIcon()}
                </ListItemIcon>
                {isShowUnderline &&
                  ((isSideNavigationOpen && children && locate < 1 && !open) ||
                    !isSideNavigationOpen) && (
                    <div
                      className={classnames(classes.cuttingLine, {
                        [classes.cuttingLineOpenMenu]: isSideNavigationOpen,
                      })}
                    />
                  )}
              </div>
              {isSideNavigationOpen && (
                <ListItemText
                  className={isSecondaryMenu ? classes.secondaryMenuListText : ''}
                  primary={realName}
                  aria-controls={realName}
                />
              )}
              {hideIcon && children && locate < 1 && isSideNavigationOpen && (
                <ArrowV
                  className={classnames(classes.keyboardArrow, {
                    [classes.keyboardArrowDown]: open,
                  })}
                />
              )}
            </div>
          </Tooltip>
        </ListItem>
        {children &&
          locate < 1 &&
          (isSideNavigationOpen ? (
            <Collapse in={open} timeout="auto" unmountOnExit>
              {children.map((item, index) => (
                <ListSlide
                  isSecondaryMenu={true}
                  key={item.name}
                  locate={!locate ? 1 : locate + 1}
                  {...item}
                  parentName={concatName}
                  history={history}
                  canOpen={isSideNavigationOpen}
                  isLastOnSecondaryMenu={index === (children || []).length - 1}
                />
              ))}
              {name === 'in-residence' && <div className={classes.cuttingLineOpenSecondMenu} />}
            </Collapse>
          ) : (
            <Menu
              id={realName}
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              {children.map((item) => (
                <MenuItem
                  selected={pathname.indexOf(item.slug) > -1}
                  key={item.name}
                  onClick={() => handleClose(pushUrl(concatName, item.slug), { auth: item.auth })}
                >
                  {t(`functions.${item.slug}`)}
                </MenuItem>
              ))}
            </Menu>
          ))}
      </List>
    )
  );
}

ListSlide.propTypes = {
  slug: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  childPermissions: PropTypes.array,
  locate: PropTypes.number,
  auth: PropTypes.array,
  parentName: PropTypes.string,
  isSecondaryMenu: PropTypes.bool,
  isLastOnSecondaryMenu: PropTypes.bool,
};

ListSlide.defaultProps = {
  locate: 0,
  childPermissions: null,
  auth: [],
  parentName: '',
};
